import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';

interface DeleteObjectDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemCount: number;
  itemName?: string;
  loading: boolean;
  error: string | null;
}

export const DeleteObjectDialog: React.FC<DeleteObjectDialogProps> = ({
  open,
  onClose,
  onConfirm,
  itemCount,
  itemName,
  loading,
  error,
}) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (!loading) {
      onConfirm();
    }
  };

  const itemDescription =
    itemCount === 1 && itemName
      ? `"${itemName}"`
      : `${itemCount} object${itemCount !== 1 ? 's' : ''}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreenMobile}
      data-testid="delete-object-dialog"
    >
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        Delete {itemCount === 1 ? 'Object' : 'Objects'}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete {itemDescription}? This will remove{' '}
          {itemCount === 1 ? 'it' : 'them'} from your catalog.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          disabled={loading}
          data-testid="delete-object-confirm"
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
